import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, ArrowRight, Plane, ShoppingBag } from "lucide-react";
import { DestinationCard, BawainerCard } from "../components/cards";
import { SectionTitle, Spinner } from "../components/common";
import RequestItemDialog from "../components/RequestItemDialog";
import api from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";

export default function Home() {
  const { t } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [dests, setDests] = useState([]);
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [reqTrip, setReqTrip] = useState(null);

  useEffect(() => {
    Promise.all([api.get("/destinations"), api.get("/trips?sort=boosted")])
      .then(([d, tr]) => { setDests(d.data); setTrips(tr.data.slice(0, 6)); })
      .finally(() => setLoading(false));
  }, []);

  const isBawainer = user?.role === "bawainer";

  return (
    <div className="space-y-8">
      {/* Hero */}
      <div className="relative rounded-3xl overflow-hidden bg-navy text-white p-6 sm:p-10">
        <div className="relative z-10 max-w-xl">
          <h1 className="font-heading font-black tracking-tighter text-3xl sm:text-4xl">
            {t("hero_title")}
          </h1>
          <p className="text-white/70 mt-2 text-sm sm:text-base">{t("hero_sub")}</p>
          <div className="mt-5 bg-white rounded-full p-1.5 flex items-center shadow-lift">
            <Search className="ml-3 text-muted-foreground" size={18} />
            <input data-testid="home-search" value={q} onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && navigate(`/explore?q=${encodeURIComponent(q)}`)}
              placeholder={t("search_ph")} className="flex-1 bg-transparent px-3 py-2 text-sm text-ink outline-none" />
            <button data-testid="home-search-btn" onClick={() => navigate(`/explore?q=${encodeURIComponent(q)}`)}
              className="rounded-full bg-lime hover:bg-lime-dark text-navy font-bold px-5 py-2.5 text-sm transition-colors">Cari</button>
          </div>
          <div className="flex flex-wrap gap-3 mt-4">
            <button onClick={() => navigate("/whos-going")} data-testid="home-find-bawainer"
              className="rounded-full bg-lime text-navy font-bold px-5 py-2.5 text-sm hover:bg-lime-dark transition-colors flex items-center gap-2">
              {t("find_bawainer")} <ArrowRight size={15} />
            </button>
            <button onClick={() => navigate(isBawainer ? "/post-trip" : "/post-request")} data-testid="home-cta-secondary"
              className="rounded-full bg-white/10 text-white font-bold px-5 py-2.5 text-sm hover:bg-white/20 transition-colors flex items-center gap-2">
              {isBawainer ? <><Plane size={15} /> {t("post_trip")}</> : <><ShoppingBag size={15} /> {t("post_request")}</>}
            </button>
          </div>
        </div>
        <div className="absolute right-0 top-0 h-full w-1/3 opacity-20 pointer-events-none"
          style={{ background: "radial-gradient(circle at 70% 30%, #B7E33A, transparent 60%)" }} />
      </div>

      {loading ? <Spinner /> : (
        <>
          <div>
            <SectionTitle action={<button onClick={() => navigate("/whos-going")} data-testid="see-all-dest" className="text-sm font-semibold text-sky">See all</button>}>
              {t("whos_going")}
            </SectionTitle>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {dests.map((d) => (
                <DestinationCard key={d.code} dest={d} onClick={() => navigate(`/whos-going?dest=${d.code}`)} />
              ))}
            </div>
          </div>

          <div>
            <SectionTitle action={<button onClick={() => navigate("/explore")} data-testid="see-all-trips" className="text-sm font-semibold text-sky">Explore</button>}>
              Featured Travelers
            </SectionTitle>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {trips.map((tr) => (
                <BawainerCard key={tr.id} trip={tr} onRequest={user?.role === "titiper" ? setReqTrip : undefined} />
              ))}
            </div>
          </div>
        </>
      )}

      {reqTrip && <RequestItemDialog trip={reqTrip} open={!!reqTrip} onOpenChange={(v) => !v && setReqTrip(null)} />}
    </div>
  );
}
